import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, X, Scale, Info, BookOpen, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeMeta = {
  do: { Icon: Check, label: "Do", cls: "pill-do" },
  dont: { Icon: X, label: "Don't", cls: "pill-dont" },
  law: { Icon: Scale, label: "Law", cls: "pill-law" },
  religion: { Icon: BookOpen, label: "Religion", cls: "pill-religion" },
  culture: { Icon: Users, label: "Culture", cls: "pill-culture" },
};

export default function CountryDetail() {
  const { slug } = useParams();
  const [country, setCountry] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/countries/${slug}`)
      .then((r) => setCountry(r.data))
      .catch(() => setError("Country not found"));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-[#475569]">{error}</p>
        <Link to="/countries" className="btn-primary mt-6 inline-flex">Back to Countries</Link>
      </div>
    );
  }

  if (!country) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-[#475569]">Loading…</div>;
  }

  return (
    <div data-testid="country-detail-page" className="fade-in">
      {/* Header */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <Link to="/countries" data-testid="back-to-countries" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] mb-8">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All countries
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{country.flag}</span>
                <div className="overline">Country Guide</div>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A]">
                {country.name}
              </h1>
              <p className="mt-4 text-lg text-[#475569] leading-relaxed">{country.tagline}</p>
              <div className="mt-6 bg-[#EEF2FF] border-l-4 border-[#002FA7] p-5">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-sm text-[#0F172A] font-medium leading-relaxed">{country.headline_tip}</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img
                src={country.image}
                alt={country.name}
                className="w-full h-72 lg:h-80 object-cover rounded-sm border border-[#E2E8F0]"
                data-testid="country-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 space-y-16">
        {country.sections.map((section) => {
          const dos = section.items.filter((it) => it.type === "do");
          const donts = section.items.filter((it) => it.type === "dont");
          const others = section.items.filter((it) => !["do", "dont"].includes(it.type));
          const renderItem = (item, i) => {
            const meta = typeMeta[item.type] || typeMeta.do;
            const Icon = meta.Icon;
            return (
              <div key={i} className="card p-5 flex gap-3" data-testid={`tip-${item.type}-${i}`}>
                <div className={`shrink-0 w-8 h-8 rounded-sm flex items-center justify-center ${meta.cls}`}>
                  <Icon className="w-4 h-4" strokeWidth={2} />
                </div>
                <div>
                  <div className="overline mb-1">{meta.label}</div>
                  <p className="text-sm text-[#0F172A] leading-relaxed">{item.text}</p>
                </div>
              </div>
            );
          };
          return (
            <div key={section.title} data-testid={`section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="mb-6">
                <div className="divider-bar" />
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]">
                  {section.title}
                </h2>
              </div>

              {(dos.length > 0 || donts.length > 0) && (
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <div className="overline mb-3 text-[#065F46]">Do · {dos.length}</div>
                    <div className="space-y-3">
                      {dos.length === 0 ? (
                        <p className="text-sm text-[#94A3B8] italic">No specific Dos in this category.</p>
                      ) : (
                        dos.map(renderItem)
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="overline mb-3 text-[#991B1B]">Don't · {donts.length}</div>
                    <div className="space-y-3">
                      {donts.length === 0 ? (
                        <p className="text-sm text-[#94A3B8] italic">No specific Don'ts in this category.</p>
                      ) : (
                        donts.map(renderItem)
                      )}
                    </div>
                  </div>
                </div>
              )}

              {others.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {others.map(renderItem)}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="bg-[#0F172A] text-white rounded-sm p-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Test what you've learned.</h3>
            <p className="text-white/70 mt-1 text-sm">10 quick questions across countries.</p>
          </div>
          <Link to="/quiz" data-testid="country-cta-quiz" className="bg-white text-[#0F172A] rounded-sm px-6 py-3 font-medium">
            Take the Quiz
          </Link>
        </div>
      </section>
    </div>
  );
}
